import {
  BUDGET_SHORTCUTS,
  formatMinutes,
  parseBudget,
  parseFreeTime,
  splitMinutes,
  TIME_SHORTCUTS,
} from "../../../shared/planFilters";

export type PlanLimitsValue = { hours: string; minutes: string; budget: string };

/** Tiempo libre (horas + minutos escribibles) y presupuesto en € escribible, con atajos rápidos. */
export function PlanLimitsInputs({
  value,
  onChange,
  timeShortcuts = TIME_SHORTCUTS,
  idPrefix = "plan",
}: {
  value: PlanLimitsValue;
  onChange: (next: PlanLimitsValue) => void;
  timeShortcuts?: number[];
  idPrefix?: string;
}) {
  const time = parseFreeTime(value.hours, value.minutes);
  const budget = parseBudget(value.budget);
  const digits = (text: string) => text.replace(/[^\d]/g, "").slice(0, 4);
  const decimal = (text: string) => text.replace(/[^\d.,]/g, "").slice(0, 8);

  return (
    <div className="limits-block">
      <p className="label">Tiempo libre</p>
      <div className="limits-fields">
        <label className="limits-field" htmlFor={`${idPrefix}-hours`}>
          <input
            id={`${idPrefix}-hours`}
            className={time.ok ? "" : "is-bad"}
            inputMode="decimal"
            autoComplete="off"
            placeholder="0"
            value={value.hours}
            onChange={(e) => onChange({ ...value, hours: decimal(e.target.value) })}
            aria-label="Horas de tiempo libre"
            aria-invalid={!time.ok}
            maxLength={5}
          />
          <span>h</span>
        </label>
        <label className="limits-field" htmlFor={`${idPrefix}-minutes`}>
          <input
            id={`${idPrefix}-minutes`}
            className={time.ok ? "" : "is-bad"}
            inputMode="numeric"
            autoComplete="off"
            placeholder="0"
            value={value.minutes}
            onChange={(e) => onChange({ ...value, minutes: digits(e.target.value) })}
            aria-label="Minutos de tiempo libre"
            aria-invalid={!time.ok}
            maxLength={4}
          />
          <span>min</span>
        </label>
      </div>
      <p className={time.ok ? "limits-hint" : "limits-error"} role={time.ok ? undefined : "alert"}>
        {time.ok ? `En total: ${formatMinutes(time.minutes)} (incluye ir y volver).` : time.error}
      </p>
      <div className="time-row">
        {timeShortcuts.map((m) => (
          <button
            key={m}
            type="button"
            className={`time-chip${time.ok && time.minutes === m ? " on" : ""}`}
            onClick={() => {
              const split = splitMinutes(m);
              onChange({ ...value, hours: split.hours, minutes: split.minutes });
            }}
          >
            {formatMinutes(m)}
          </button>
        ))}
      </div>

      <p className="label">Presupuesto por persona</p>
      <div className="limits-fields">
        <label className="limits-field limits-field--wide" htmlFor={`${idPrefix}-budget`}>
          <input
            id={`${idPrefix}-budget`}
            className={budget.ok ? "" : "is-bad"}
            inputMode="decimal"
            autoComplete="off"
            placeholder="Sin límite"
            value={value.budget}
            onChange={(e) => onChange({ ...value, budget: decimal(e.target.value) })}
            aria-label="Presupuesto máximo en euros"
            aria-invalid={!budget.ok}
            maxLength={8}
          />
          <span>€</span>
        </label>
      </div>
      <p className={budget.ok ? "limits-hint" : "limits-error"} role={budget.ok ? undefined : "alert"}>
        {budget.ok
          ? budget.budget == null
            ? "Sin límite de gasto. Escribe una cantidad para limitarlo (0 = solo gratis)."
            : budget.budget === 0
              ? "Solo planes gratis."
              : `Hasta ${String(budget.budget).replace(".", ",")} € por persona (precios orientativos).`
          : budget.error}
      </p>
      <div className="time-row">
        {BUDGET_SHORTCUTS.map((b) => {
          const label = b == null ? "Sin límite" : b === 0 ? "Gratis" : `${b} €`;
          return (
            <button
              key={label}
              type="button"
              className={`time-chip${budget.ok && budget.budget === b ? " on" : ""}`}
              onClick={() => onChange({ ...value, budget: b == null ? "" : String(b) })}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
